import { useEffect, useState } from 'react';

export default function HeroSection() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-end overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/3cf7f061-b04c-417f-8914-2b61927e7230_compressed_G_Image_v9tdknv9tdknv9td.webp"
          alt="鳴箏古箏教學"
          className="w-full h-full object-cover object-center"
        />
        {/* Layered overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e0b08]/80 via-[#0e0b08]/20 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0e0b08]/40 via-transparent to-transparent"></div>
        {/* Extra warm overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#3a2e22]/30 via-[#3a2e22]/15 to-transparent"></div>
      </div>

      {/* Top Logo area - visible on hero */}
      <div className={`absolute top-24 left-0 right-0 flex justify-center transition-all duration-1000 ${visible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="flex flex-col items-center gap-3">
          <img
            src="https://static.readdy.ai/image/c58d2dc5fdc4f4caa90264537c733184/e33f307f87b4fb2fac52df6e599e8335.png"
            alt="鳴箏"
            className="h-16 w-auto object-contain brightness-0 invert opacity-90"
          />
        </div>
      </div>

      {/* Main Content - bottom aligned with soft text-backing glow */}
      <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
        <div className="absolute bottom-0 left-0 right-0 h-[320px] rounded-b-none bg-gradient-to-t from-[#1a1410]/30 via-[#2c2018]/15 to-transparent pointer-events-none md:w-[60%] w-full"></div>
        <div className="relative max-w-3xl">
          {/* Badge */}
          <div
            className={`mb-7 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: '0.2s' }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#e8d8c4]/50 text-[#e8d8c4]/90 text-[0.8125rem] tracking-[0.12em] font-medium backdrop-blur-sm bg-black/10"
              style={{ textShadow: '0 1px 3px rgba(0,0,0,0.25)' }}
            >
              古箏教學・陪伴式學習・選箏諮詢
            </span>
          </div>

          {/* Brand Name */}
          <div
            className={`mb-5 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: '0.35s' }}
          >
            <h1
              className="font-serif-tc text-white leading-[1.2]"
              style={{ fontSize: 'clamp(2rem, 5.5vw, 2.75rem)', letterSpacing: '0.18em', textShadow: '0 2px 12px rgba(0,0,0,0.35), 0 0 40px rgba(0,0,0,0.15)' }}
            >
              把古箏與音樂，<br className="sm:hidden" />慢慢融入生活裡
            </h1>
          </div>

          {/* Sub tagline */}
          <div
            className={`mb-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: '0.5s' }}
          >
            <p
              className="text-[#e8dcc8]/85 leading-[1.8] max-w-lg"
              style={{ fontSize: '1.0625rem', textShadow: '0 1px 6px rgba(0,0,0,0.3)' }}
            >
              讓古箏與音樂，自然成為日常的一部分。<br />
              不急著學會，只需要一個願意開始的心。
            </p>
          </div>

          {/* CTA Buttons */}
          <div
            className={`flex flex-col sm:flex-row gap-3 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: '0.65s' }}
          >
            <a
              href="https://line.me"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#f5f0e8] text-[#2c2420] text-[1rem] font-medium hover:bg-white transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.25)' }}
            >
              <i className="ri-line-fill text-[#06C755]"></i>
              加入 LINE@ 諮詢
            </a>
            <a
              href="#about-brand"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border-[1.5px] border-[#e8dcc8]/70 text-[#e8dcc8] hover:border-[#f5f0e8]/90 hover:text-[#f5f0e8] text-[1rem] font-medium transition-all duration-300 backdrop-blur-sm whitespace-nowrap cursor-pointer"
              style={{ textShadow: '0 1px 4px rgba(0,0,0,0.25)' }}
            >
              看看這裡適不適合我
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-down-line text-sm"></i>
              </div>
            </a>
          </div>

          {/* Location Quick Info */}
          <div
            className={`flex flex-wrap items-center gap-5 mt-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: '0.8s' }}
          >
            <a
              href="/contact#location"
              className="flex items-center gap-2 text-[#d4c8b4]/80 hover:text-[#e8dcc8] transition-colors cursor-pointer group"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-map-pin-2-line text-sm"></i>
              </div>
              <span className="text-[0.875rem] tracking-wide">台中豐原・實體教學</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-xs group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"></i>
              </div>
            </a>
            <span className="text-[#c8b89a]/25 hidden sm:block">|</span>
            <a
              href="/courses"
              className="flex items-center gap-2 text-[#d4c8b4]/80 hover:text-[#e8dcc8] transition-colors cursor-pointer group"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-sm"></i>
              </div>
              <span className="text-[0.875rem] tracking-wide">查看課程費用</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-xs group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"></i>
              </div>
            </a>
          </div>
        </div>

        {/* Right side decorative text */}
        <div
          className={`hidden lg:flex absolute right-20 bottom-24 flex-col items-end gap-1 transition-all duration-700 ${visible ? 'opacity-100' : 'opacity-0'}`}
          style={{ transitionDelay: '0.8s' }}
        >
          <p className="text-[#d4c8b4]/60 text-[0.75rem] tracking-[0.3em] uppercase">Mingzheng</p>
          <p className="text-[#d4c8b4]/50 text-[0.6875rem] tracking-[0.15em]">古箏教學品牌</p>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-float">
        <div className="w-px h-12 bg-gradient-to-b from-transparent to-[#c8b89a]/40"></div>
        <div className="w-1 h-1 rounded-full bg-[#c8b89a]/50"></div>
      </div>
    </section>
  );
}
